import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { getCheckoutStatus } from '../lib/api';
import { useCart } from '../context/CartContext';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [state, setState] = useState({ status: 'polling', orderId: null, error: '' });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', error: 'Missing session id', orderId: null });
      return;
    }
    let attempts = 0;
    const maxAttempts = 12; // ~24s
    const tick = async () => {
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === 'paid') {
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
            localStorage.removeItem('hh_pending_order');
          }
          setState({ status: 'paid', orderId: data.orderId, error: '' });
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'failed') {
          setState({ status: 'failed', orderId: data.orderId, error: 'Payment did not complete.' });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: 'timeout', orderId: data.orderId, error: 'Payment status check timed out. Check your email for confirmation.' });
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        if (attempts >= maxAttempts) {
          setState({ status: 'error', orderId: null, error: 'Could not verify payment. Please contact support.' });
          return;
        }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [sessionId, clearCart]);

  return (
    <div className="bg-black text-white min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {state.status === 'polling' && (
          <>
            <Loader2 className="mx-auto text-[#909090] animate-spin mb-5" size={48} />
            <h2 className="text-2xl font-bold mb-2">Confirming your payment…</h2>
            <p className="text-zinc-400 mb-6 text-sm">Just a moment while we verify with Stripe.</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <div className="w-16 h-16 mx-auto bg-[#909090] rounded-full flex items-center justify-center mb-5">
              <Check className="text-black" size={32} />
            </div>
            <h2 className="text-3xl font-bold mb-3">Payment successful!</h2>
            <p className="text-zinc-400 mb-1">Your order ID is <span className="text-[#909090] font-bold">{state.orderId}</span></p>
            <p className="text-zinc-500 text-sm mb-6">A confirmation email has been sent. Thank you for choosing Hydro Health.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate(`/track?orderId=${state.orderId}`)} className="bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-6 py-3 rounded transition-colors">
                Track order
              </button>
              <Link to="/shop" className="border border-zinc-700 hover:border-[#909090] text-white font-medium px-6 py-3 rounded transition-colors">
                Continue shopping
              </Link>
            </div>
          </>
        )}
        {(state.status === 'failed' || state.status === 'error' || state.status === 'timeout') && (
          <>
            <AlertCircle className="mx-auto text-amber-400 mb-5" size={48} />
            <h2 className="text-2xl font-bold mb-2">
              {state.status === 'timeout' ? 'Still processing...' : 'Payment issue'}
            </h2>
            <p className="text-zinc-400 mb-6 text-sm">{state.error}</p>
            <div className="flex gap-3 justify-center">
              <Link to="/checkout" className="bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-6 py-3 rounded transition-colors">
                Back to checkout
              </Link>
              <Link to="/contact" className="border border-zinc-700 hover:border-[#909090] text-white font-medium px-6 py-3 rounded transition-colors">
                Contact us
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
